import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import type { PageDto } from '@/lib/pagination';
import { useAuth } from '@/lib/auth/AuthContext';

export interface ActionExecutionOut {
  action: string;
  completed_at: string | null;
  created_at: string;
  destination_id: string | null;
  document_id: string | null;
  encounter_id: string;
  executed_by: string | null;
  id: string;
  organization_id: string;
  result: Record<string, unknown> | null;
  started_at: string | null;
  status: string;
  template_id: string | null;
}

export interface DocumentPatientSummary {
  id: string;
  mrn: string;
  name: string | null;
}

export interface DocumentTemplateSummary {
  id: string;
  name: string;
}

export interface GeneratedDocumentOut {
  additional_context: Record<string, unknown> | null;
  content: string;
  created_at: string;
  created_by: string | null;
  deleted_at: string | null;
  generated_at: string;
  id: string;
  organization_id: string;
  patient: DocumentPatientSummary | null;
  /** null when the document is in the Inbox (Plan 06). */
  patient_id: string | null;
  status: string;
  tags?: import('./tags-api').TagDto[];
  template: DocumentTemplateSummary | null;
  template_id: string | null;
  updated_at: string;
}

export interface GeneratedDocumentSourceOut {
  document_id: string;
  encounter_id: string;
  id: string;
  linked_at: string;
}

export type PaginatedDocuments = PageDto<GeneratedDocumentOut>;

export function useActionExecutions(params?: { encounter_id?: string; status?: string }) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    enabled: !!orgId,
    queryKey: ['action-executions', orgId, params],
    queryFn: async () => {
      const { data } = await clientApi.get<PageDto<ActionExecutionOut>>(`${orgUrl(orgId!)}/action-executions`, {
        params,
      });
      return data;
    },
  });
}

export function useAssignDocumentToPatient() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ documentId, patient_id }: { documentId: string; patient_id: string }) => {
      const { data } = await clientApi.post<GeneratedDocumentOut>(`${orgUrl(orgId!)}/documents/${documentId}/assign`, {
        patient_id,
      });
      return data;
    },
    onSuccess: (_, variables) => {
      // Both the Inbox query and the target patient's chart query are scoped
      // under ['documents', orgId, ...] — invalidate the whole subtree so the
      // row leaves the Inbox and appears under the patient timeline.
      queryClient.invalidateQueries({ queryKey: ['documents', orgId] });
      queryClient.invalidateQueries({ queryKey: ['documents', orgId, variables.documentId] });
    },
  });
}

export function useBulkDeleteDocuments() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', orgId] });
    },
    mutationFn: async (documentIds: string[]) => {
      const results = await Promise.allSettled(
        documentIds.map((id) => clientApi.delete(`${orgUrl(orgId!)}/documents/${id}`)),
      );
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        throw new Error(`Failed to delete ${failed.length} of ${documentIds.length} documents`);
      }
    },
  });
}

export function useDeleteDocument() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', orgId] });
    },
    mutationFn: async (documentId: string) => {
      await clientApi.delete(`${orgUrl(orgId!)}/documents/${documentId}`);
    },
  });
}

export function useDocument(documentId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    enabled: !!orgId && !!documentId,
    queryKey: ['documents', orgId, documentId],
    queryFn: async () => {
      const { data } = await clientApi.get<GeneratedDocumentOut>(`${orgUrl(orgId!)}/documents/${documentId}`);
      return data;
    },
  });
}

export function useDocuments(params?: {
  limit?: number;
  offset?: number;
  patient_id?: string;
  status?: string;
  /** When true, only return orphan (Inbox) documents — patient_id IS NULL. */
  inbox?: boolean;
}) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    enabled: !!orgId,
    queryKey: ['documents', orgId, params],
    queryFn: async () => {
      const { data } = await clientApi.get<PaginatedDocuments>(`${orgUrl(orgId!)}/documents`, { params });
      return data;
    },
  });
}

export function useDocumentSources(documentId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    enabled: !!orgId && !!documentId,
    queryKey: ['documents', orgId, documentId, 'sources'],
    queryFn: async () => {
      const { data } = await clientApi.get<GeneratedDocumentSourceOut[]>(
        `${orgUrl(orgId!)}/documents/${documentId}/sources`,
      );
      return data;
    },
  });
}

export function useGenerateDocument() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', orgId] });
      queryClient.invalidateQueries({ queryKey: ['action-executions', orgId] });
    },
    mutationFn: async (data: {
      template_id: string;
      patient_id: string;
      encounter_ids: string[];
      additional_context?: Record<string, unknown> | null;
    }) => {
      const { data: result } = await clientApi.post<GeneratedDocumentOut>(`${orgUrl(orgId!)}/documents/generate`, data);
      return result;
    },
  });
}

export function useUpdateDocument() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['documents', orgId] });
      queryClient.invalidateQueries({ queryKey: ['documents', orgId, variables.documentId] });
    },
    mutationFn: async ({ data, documentId }: { documentId: string; data: { content?: string; status?: string } }) => {
      const { data: result } = await clientApi.patch<GeneratedDocumentOut>(
        `${orgUrl(orgId!)}/documents/${documentId}`,
        data,
      );
      return result;
    },
  });
}

function orgUrl(orgId: string) {
  return `/api/v1/organizations/${orgId}`;
}
